import { PageWithNavigation } from '@/src/components';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom-v5';
import { Margin, P, Stack, Strong } from '@ovotech/element-v5';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import {
  useChargeAnytimeContext,
  PlanDetails,
  useEligibilityQuery,
  useChargeAnytimeProducts,
  EV,
  isSelectedPlanMonthlyPlan,
  isSelectedPlanPaygPlan,
  EligibleProduct,
  getChargeAnytimeStandardPlansFaqLink,
  NOT_LISTED,
} from '@monovo/ev-anytime';
import {
  HelpText,
  MainContentCard,
  TabButton,
  TabButtonGroup,
  PlanHeaderRow,
  PlanHeaderText,
  PlanHeaderTitle,
  PlanHeaderBody,
  PlanToggleWrapper,
  GroupedPlansContainer,
  PlanGroupSection,
  PlanGroupHeading,
  KwikFitNoticeWrapper,
} from './ProductSelection.styled';
import { TechnicalGlitchPage } from '@/src/pages/AnytimeEligibilityV2/pages/TechnicalGlitchPage/TechnicalGlitchPage';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import { PlanSelector, ProductType } from '../../components/PlanSelector';
import { PageHeaderV2 } from '../../components/PageHeaderV2';
import { PlanGrid } from '../../components/PlanGrid';
import Footnote from '../../components/Footnote';
import StandardPlansViaChargerNote from '../../components/StandardPlansViaChargerNote';
import { KwikFitMaintenanceNotification } from '../../components/KwikFitMaintenanceNotification';
import { useAnalytics } from '@ovotech/ui-tools';

const analyticsEventProperties = {
  Screen: EV.SCREENS.AVAILABLE_ADD_ONS,
  journeyName: 'Charge Anytime',
};
const segments: Record<ProductType, { key: ProductType; label: string }> = {
  monthly: { key: 'monthly', label: 'Pay monthly' },
  payg: { key: 'payg', label: 'Pay as you go' },
} as const;

export const ProductSelection = () => {
  const history = useHistory();
  const {
    setSelectedPlanId,
    smartCharger,
    make,
    showKwhVariant,
    integrationType,
    eligibleProducts,
    standardPlansAvailableViaCharger,
    standardPlansViaCharger,
    vehicleDeviceId,
    setVehicleDeviceId,
    setMake,
    setModel,
    setYear,
    setType,
    setIntegrationType,
    setPrioritisedConnectionDevice,
  } = useChargeAnytimeContext();
  const { data: eligibilityData, refetch: checkEligibility } =
    useEligibilityQuery();
  // The eligibility response is the source of truth for the connection basis.
  // Read from it rather than snapshotting context, which we overwrite on select.
  // EligibilityV2Response is a union — INCOMPATIBLE carries no `connection`.
  const eligibilityConnection =
    eligibilityData && eligibilityData.compatibilityStatus !== 'INCOMPATIBLE'
      ? eligibilityData.connection
      : null;
  const isVolvo = integrationType === 'Volvo';
  const { dispatch: analyticsDispatch } = useAnalytics();

  const {
    data: products,
    isLoading,
    isError,
  } = useChargeAnytimeProducts('all');

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (eligibleProducts) {
      return products.filter(plan =>
        eligibleProducts.includes(plan.id as EligibleProduct),
      );
    }
    return products.filter(
      plan => plan.productType === 'monthly' || plan.productType === 'payg',
    );
  }, [products, eligibleProducts]);

  const monthlyPlans = useMemo(
    () =>
      filteredProducts.filter((plan: PlanDetails) =>
        isSelectedPlanMonthlyPlan(plan.id),
      ),
    [filteredProducts],
  );
  const paygPlans = useMemo(
    () =>
      filteredProducts.filter((plan: PlanDetails) =>
        isSelectedPlanPaygPlan(plan.id),
      ),
    [filteredProducts],
  );
  const paygVolvoProduct = useMemo(
    () =>
      products?.filter((plan: PlanDetails) => plan.id === 'payg-volvo') || [],
    [products],
  );

  const showMonthlyPlans = monthlyPlans.length > 0;
  const showPaygPlans = paygPlans.length > 0;

  // The "OVO plans" the backend offers via the charger. Anything already in the
  // monthly grid above is excluded so no plan renders twice.
  const ovoPlansViaCharger = useMemo(() => {
    if (!products || !standardPlansViaCharger) return [];
    const alreadyShown = new Set(monthlyPlans.map(plan => plan.id));
    return products.filter(
      (plan: PlanDetails) =>
        standardPlansViaCharger.products.includes(plan.id as EligibleProduct) &&
        isSelectedPlanMonthlyPlan(plan.id) &&
        !alreadyShown.has(plan.id),
    );
  }, [products, standardPlansViaCharger, monthlyPlans]);

  // OVO plans: purely what the backend sent. Nothing else. This also drives the
  // updated heading and subheading, since that copy offers a choice between a
  // partner plan and an OVO plan — it only makes sense when both are on the
  // page. Volvo qualifies: PAYG-only, but the backend sends OVO plans.
  const showOvoPlans = ovoPlansViaCharger.length > 0;

  // Default to monthly. The useState initialiser runs once on mount, before
  // eligibility products have loaded, so we must not guess PAYG here — that left
  // the PAYG tab wrongly selected once monthly plans arrived. The effect below
  // only switches to PAYG when we actually know there are no monthly plans.
  const [activeTab, setActiveTab] = useState<ProductType>(ProductType.MONTHLY);

  // The OVO plans list is always monthly plans. When there's no monthly tab to
  // choose between (e.g. Volvo, who only ever has a PAYG plan of their own),
  // there's nothing to hide them behind, so they stay visible. But once the
  // customer has their own monthly plans too — i.e. there's an actual
  // Monthly/PAYG toggle on the page — switching to the PAYG tab should hide
  // the monthly OVO plans
  const showOvoPlansForActiveTab =
    showOvoPlans && (activeTab === ProductType.MONTHLY || !showMonthlyPlans);

  // The notice belongs beside plans that actually carry the perk. `promotion`
  // is the Kwik Fit slot (same check SelectedPlan makes), and partner/OEM
  // plans don't have it - a Kia customer with an unlisted charger sees only
  // their OEM plan and no OVO plans group, so they get no notice at all.
  const hasKwikFitPerk = (plans: PlanDetails[]) =>
    plans.some(plan => 'promotion' in plan.features);

  // Prefer the OVO plans group when it's on screen; otherwise sit above the
  // monthly grid. Exclusive, so the notice never renders twice.
  const showMonthlyGrid = activeTab === ProductType.MONTHLY && showMonthlyPlans;
  const showKwikFitNoticeInOvoGroup =
    showOvoPlansForActiveTab && hasKwikFitPerk(ovoPlansViaCharger);
  const showKwikFitNoticeAboveGrid =
    showMonthlyGrid &&
    !showKwikFitNoticeInOvoGroup &&
    hasKwikFitPerk(monthlyPlans);

  useEffect(() => {
    if (!showMonthlyPlans && showPaygPlans) {
      setActiveTab(ProductType.PAYG);
    } else if (showMonthlyPlans && !showPaygPlans) {
      setActiveTab(ProductType.MONTHLY);
    }
  }, [showMonthlyPlans, showPaygPlans]);

  const handleSelectPlan = useCallback(
    (planId: string) => {
      const planName = products?.find(plan => plan.id === planId)?.title || '';
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_AVAILABLE_ADDONS_NEXT_CLICKED,
        properties: {
          ...analyticsEventProperties,
          planName: planName,
        },
      });

      // The connection basis follows the plan just chosen, every time. Setting it
      // on both branches means going back and picking a different plan can't
      // leave a stale basis behind — e.g. choosing an OVO plan and then returning
      // to pick the Volvo one must restore Volvo/VEHICLE.
      if (
        standardPlansViaCharger?.products.includes(planId as EligibleProduct)
      ) {
        setIntegrationType(
          standardPlansViaCharger.connectionBasis.integrationType,
        );
        setPrioritisedConnectionDevice(
          standardPlansViaCharger.connectionBasis.prioritisedDevice,
        );
      } else if (eligibilityConnection) {
        // Only restore when we actually have a connection to restore from —
        // otherwise leave whatever the eligibility check already put in context,
        // rather than nulling it.
        setIntegrationType(
          eligibilityConnection.integration?.integrationType ?? null,
        );
        setPrioritisedConnectionDevice(
          eligibilityConnection.prioritisedDevice ?? null,
        );
      }

      setSelectedPlanId(planId as PlanDetails['id']);
      history.push(`${ROUTE_ANYTIME_ELIGIBILITY.SELECTED_PLAN}`);
    },
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      history,
      products,
      setSelectedPlanId,
      standardPlansViaCharger,
      eligibilityConnection,
      setIntegrationType,
      setPrioritisedConnectionDevice,
    ],
  );

  const handleTabChange = useCallback(
    (tab: ProductType) => {
      if (tab === activeTab) {
        return;
      }
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_AVAILABLE_ADDONS_TOGGLE_CLICKED,
        properties: {
          ...analyticsEventProperties,
          selectedToggle: segments[tab].label,
        },
      });
      setActiveTab(tab);
    },
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab],
  );

  useEffect(
    () => {
      // event on mount
      analyticsDispatch({
        type: 'view',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_AVAILABLE_ADDONS_SCREEN_VIEWED,
        properties: analyticsEventProperties,
      });
    },
    // `dispatch` method is not memoized, and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (isVolvo) {
      setActiveTab(ProductType.PAYG);
    }
  }, [isVolvo]);

  useEffect(() => {
    if (!smartCharger || !make) {
      history.push(ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER);
    }
  }, [smartCharger, make, history]);

  // Re-enter as "EV not listed" (make stays truthy so the charger-step guard
  // doesn't fire), then re-run eligibility charger-only. The refetch is deferred
  // to an effect so it sees the cleared vehicle state, not the stale body.
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectStarted = useRef(false);

  const handleConnectViaCharger = useCallback(() => {
    setVehicleDeviceId(null);
    setMake(NOT_LISTED);
    setModel(null);
    setYear(null);
    setType(null);
    setIsReconnecting(true);
  }, [setVehicleDeviceId, setMake, setModel, setYear, setType]);

  useEffect(() => {
    if (
      !isReconnecting ||
      vehicleDeviceId !== null ||
      reconnectStarted.current
    ) {
      return;
    }
    reconnectStarted.current = true;
    checkEligibility().finally(() => {
      reconnectStarted.current = false;
      setIsReconnecting(false);
      // Reset to monthly (auto-select effect corrects to PAYG if no monthly).
      setActiveTab(ProductType.MONTHLY);
      history.push(ROUTE_ANYTIME_ELIGIBILITY.PRODUCT_SELECTION);
    });
  }, [isReconnecting, vehicleDeviceId, checkEligibility, history]);

  if (isReconnecting) {
    return <Loading title="Checking eligibility" />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (!products || isError || !eligibilityData) {
    return <TechnicalGlitchPage header={'Charge Anytime'} />;
  }

  const title = showOvoPlans
    ? 'Our plans'
    : isVolvo
    ? 'Enjoy flexibility with pay as you go'
    : 'Good news!';

  return (
    <>
      <PageWithNavigation title="EV Charge Anytime - Eligibility">
        <Stack spaceBetween={8}>
          <PageHeaderV2 title={title} testId="product-selection-header" />
          <MainContentCard>
            {showOvoPlans ? (
              <PlanHeaderRow>
                <PlanHeaderText>
                  <PlanHeaderTitle data-testid="product-selection-subheader">
                    Good news — your car qualifies for a better rate
                  </PlanHeaderTitle>
                  <PlanHeaderBody>
                    Charge for less than your usual home tariff rate. Choose a
                    plan made for your car, or an OVO plan.
                  </PlanHeaderBody>
                </PlanHeaderText>
                {showMonthlyPlans && showPaygPlans && (
                  <PlanToggleWrapper>
                    <TabButtonGroup>
                      <TabButton
                        isActive={activeTab === 'monthly'}
                        onClick={() => handleTabChange(ProductType.MONTHLY)}
                      >
                        Pay monthly
                      </TabButton>
                      <TabButton
                        isActive={activeTab === 'payg'}
                        onClick={() => handleTabChange(ProductType.PAYG)}
                      >
                        Pay as you go
                      </TabButton>
                    </TabButtonGroup>
                  </PlanToggleWrapper>
                )}
              </PlanHeaderRow>
            ) : (
              showMonthlyPlans &&
              showPaygPlans && (
                <PlanSelector
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  heading="You can charge for less than your usual home tariff rate – take your pick."
                  showMonthlyPlans={showMonthlyPlans}
                />
              )
            )}

            {isVolvo && paygVolvoProduct.length > 0 && (
              <Margin horizontal={4} vertical={showOvoPlans ? 0 : 8}>
                <P>
                  Enjoy <Strong>pay as you go</Strong> at half our standard
                  rate, so{' '}
                  <Strong>just {paygVolvoProduct[0].pricePerKwh}/kWh.</Strong>
                </P>
              </Margin>
            )}

            {showKwikFitNoticeAboveGrid && (
              <KwikFitNoticeWrapper>
                <KwikFitMaintenanceNotification />
              </KwikFitNoticeWrapper>
            )}

            {showMonthlyGrid && (
              <PlanGrid
                plans={monthlyPlans}
                ctaText="Select plan"
                onCtaClick={handleSelectPlan}
                variant="choose-plan"
              />
            )}
            {activeTab === ProductType.PAYG && showPaygPlans && (
              <PlanGrid
                plans={paygPlans}
                ctaText="Select plan"
                onCtaClick={handleSelectPlan}
                variant="choose-plan"
              />
            )}
            {showOvoPlansForActiveTab && (
              <GroupedPlansContainer>
                <PlanGroupSection>
                  <PlanGroupHeading>OVO plans</PlanGroupHeading>
                  {showKwikFitNoticeInOvoGroup && (
                    <KwikFitMaintenanceNotification />
                  )}
                  <StandardPlansViaChargerNote />
                  <PlanGrid
                    plans={ovoPlansViaCharger}
                    ctaText="Select plan"
                    onCtaClick={handleSelectPlan}
                    variant="choose-plan"
                  />
                </PlanGroupSection>
              </GroupedPlansContainer>
            )}
          </MainContentCard>

          <Footnote
            productType={activeTab as ProductType}
            showKwhVariant={showKwhVariant}
          />

          {/* Hidden when the OVO plans are already on the page. */}
          {!showOvoPlans && standardPlansAvailableViaCharger === true && (
            <StandardPlansViaChargerNote
              faqLink={getChargeAnytimeStandardPlansFaqLink(integrationType)}
              onConnectViaCharger={handleConnectViaCharger}
            />
          )}
        </Stack>
      </PageWithNavigation>
      <HelpText>Need help? You can get support on our help centre</HelpText>
    </>
  );
};
